<script setup>
import { ref, reactive, shallowRef, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import BaseBubble from './Base.vue';
import MessageStatus from '../MessageStatus.vue';
import { useMessengerStatus } from '../useMessengerStatus';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { getVisualViewport } from 'dashboard/helper/dashboardZoom';
import MessageReactionBar from 'dashboard/components/widgets/conversation/MessageReactionBar.vue';
import MessageReactions from '../MessageReactions.vue';
import MessageReactionsModal from '../MessageReactionsModal.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import LottieStickerCanvas from './LottieStickerCanvas.vue';
import StickerLoopCanvas from './StickerLoopCanvas.vue';
import { isLottieStickerAttachment } from 'dashboard/helper/lottieStickerPlayer';
import { STICKER_BOX_PX } from './mediaAspectLayout';
import { useMessageContext } from '../provider.js';
import {
  useDisplaySenderName,
  useGroupParticipantColor,
} from '../useDisplaySenderName.js';
import {
  computeMenuAnchor,
  getDashboardZoom,
  fixedPosition,
} from '../contextMenuAnchor.js';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS, MESSAGE_TYPES, ORIENTATION } from '../constants';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { isMessagePinned } from 'dashboard/helper/pinnedMessages';

// Aceleres: 2+ stickers consecutivos, estilo WhatsApp — 2 por linha, cada
// um repetindo a MESMA estrutura de um sticker sozinho: nome do remetente
// em cima, imagem sem corte num quadro fixo de 237.5x237.5, horário embaixo
// COM o fundo verde/branco do balão por trás (não sobreposto por cima da
// imagem). Diferente do álbum de fotos (ImageAlbum.vue), que corta em
// quadrado dentro de UM cartão só: aqui cada sticker é seu próprio cartão
// completo, só que 2 lado a lado em vez de empilhados.
//
// Não dá pra reusar o BaseBubble por tile pra isso: o MessageMeta dele
// SEMPRE fica `absolute` (sobreposto), mesmo sem media-caption — é assim
// que a bolha de sticker sozinho já funciona hoje. Por isso o card de
// cada tile é montado na mão aqui (nome + imagem + horário em fluxo
// normal, cada um na sua própria linha), em vez de tentar torcer o
// BaseBubble pra um comportamento que ele não tem.

// A medida vem de mediaAspectLayout: o visualizador usa a mesma.

const {
  albumMessages,
  orientation,
  currentUserId,
  inboxSupportsReplyTo,
  currentChatAttachments,
} = useMessageContext();
const { isMessenger } = useMessengerStatus(
  computed(() => albumMessages?.value?.[0]?.conversationId)
);

// Aceleres 2026-08-17: por anexo, se o StickerLoopCanvas ja assumiu o desenho
// da figurinha animada. O album mostra duas figurinhas ao mesmo tempo e cada
// uma decide sozinha — uma pode ser animada e a outra parada.
const activeStickerCanvas = reactive({});

const store = useStore();
const currentAccountIdGetter = useMapGetter('getCurrentAccountId');
const conversationGetter = useMapGetter('getConversationById');
const reactionChannelType = computed(() => {
  const firstMessage = albumMessages?.value?.[0];
  const conversation = firstMessage?.conversationId
    ? conversationGetter.value(firstMessage.conversationId) || {}
    : {};
  const inboxId = conversation.inbox_id ?? conversation.inboxId;
  const getInbox = store.getters['inboxes/getInbox'];
  return inboxId && typeof getInbox === 'function'
    ? getInbox(inboxId)?.channel_type || ''
    : '';
});

const albumItems = computed(() => {
  const list = albumMessages?.value || [];
  return list
    .map(message => {
      const attachment =
        (message.attachments && message.attachments[0]) || null;
      return {
        message,
        attachment,
        // Aceleres: a figurinha VETORIAL (Lottie) entra no par como qualquer
        // outra, mas o anexo dela é a animação em JSON — não há imagem para
        // pôr num <img>. Quem a desenha é o mesmo canvas da bolha sozinha.
        isLottie: isLottieStickerAttachment(attachment),
      };
    })
    .filter(item => item.attachment);
});

// A galeria não sabe abrir uma animação, e a bolha da vetorial sozinha também
// não abre nenhuma (ver LottieSticker.vue): as vetoriais ficam fora da
// navegação, senão um <img> vazio entraria no meio das figurinhas de imagem.
// Aceleres: todo o run tem o mesmo remetente (garantido no agrupamento em
// MessageList.vue), então o nome do contexto vale pros dois tiles.
//
// Usa a MESMA resolução do BaseBubble. Ler `sender.name` direto aqui mostrava
// o dono do token admin da ponte WAME no lugar do rótulo do inbox, então o par
// de figurinhas trocava de autor exibido só por ter sido agrupado.
const senderName = useDisplaySenderName();
const groupSenderColor = useGroupParticipantColor(senderName);
// Aceleres 2026-09-09: o rotulo fecha com dois pontos, como nas outras bolhas
// (ver `senderNameLabel` no Base.vue). `senderName` continua a ser o nome
// limpo — e ele que alimenta a cor do participante logo acima.
const senderNameLabel = computed(() =>
  senderName.value ? `${senderName.value}:` : senderName.value
);

const tileBackgroundClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? 'bg-[#d9fdd3]' : 'bg-white'
);
const tileRoundedClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT
    ? 'rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm'
    : 'rounded-xl'
);

const readableTime = message => messageTimestamp(message?.createdAt, 'HH:mm');

const statusToShow = message => {
  if (
    message?.messageType !== MESSAGE_TYPES.OUTGOING ||
    message?.private ||
    message?.status === MESSAGE_STATUS.FAILED
  ) {
    return null;
  }

  if (
    [
      MESSAGE_STATUS.SENT,
      MESSAGE_STATUS.DELIVERED,
      MESSAGE_STATUS.READ,
    ].includes(message.status)
  ) {
    return message.status;
  }

  return MESSAGE_STATUS.PROGRESS;
};

// Aceleres: menu de contexto por sticker. Message.vue só sabe abrir o menu
// pra UMA mensagem raiz (a última do par, ver componentToRender); aqui cada
// tile guarda em `activeContextItem` QUAL sticker foi clicado e abre o MESMO
// componente de menu (ContextMenu = MessageContextMenu.vue) apontando pra
// essa mensagem especificamente — assim "encaminhar"/"reagir"/"favoritar"
// no sticker 1 nunca afeta o sticker 2, e vice-versa.
const showTileContextMenu = ref(false);
const tileContextMenuPosition = ref({});
const activeContextItem = ref(null);

// Aceleres 2026-08-11: pill de reacao rapida (a "carita"), uma por sticker
// do par — antes so existia a flechinha (menu completo). Mesmo padrao de
// Message.vue (smile + pill via <Teleport to="body"> + position:fixed),
// so que aqui o alvo muda por tile: reaproveita `activeContextItem` (o
// mesmo estado que a flechinha ja usa) pra saber EM QUAL sticker a pill
// esta agindo, entao `handleTileReact` funciona igual pros dois gatilhos
// sem duplicar o "qual mensagem" em dois lugares.
// Aceleres 2026-08-12: as reacoes de CADA sticker do par.
//
// O par inteiro e renderizado por um unico <Message>, montado com
// `item.lastMessage` (ver MessageList.vue), entao a fila de reacoes generica
// do Message.vue so conhece as reacoes do ULTIMO sticker — e desenha a pilula
// debaixo do par, nao do sticker que a recebeu. Na pratica: reagir ao primeiro
// sticker nao mostrava nada, e reagir ao segundo mostrava a pilula no lugar
// errado. Aqui cada tile desenha a sua, sob o proprio sticker; a fila generica
// e suprimida para o album (ver `isStickerAlbumBubble` no Message.vue).
const reactionsOf = message => message?.contentAttributes?.reactions || [];
const showReactionsModal = ref(false);
const reactionsAnchorEl = shallowRef(null);

const openTileReactions = (element, item) => {
  // Reaproveita `activeContextItem` (o mesmo estado da flechinha e da carita)
  // para o modal saber de qual sticker sao as reacoes mostradas.
  activeContextItem.value = item;
  reactionsAnchorEl.value = element ?? null;
  showReactionsModal.value = true;
};

const showQuickReact = ref(false);
const albumGridRef = ref(null);
const quickReactRef = ref(null);
const pillStyle = ref({});
const pillDirection = ref('up');
const initialShowPicker = ref(false);
let quickReactOutsideHandler = null;
let quickReactScrollHandler = null;
let quickReactKeyHandler = null;
let quickReactViewportHandler = null;

function getQuickReactViewportSize() {
  const zoom = getDashboardZoom();
  const rootRect = document.documentElement.getBoundingClientRect();

  return {
    zoom,
    // Aceleres 2026-08-25: o fallback multiplicava a viewport pelo zoom. Ela ja
    // vem em espaco visual, tal como o `rootRect` — ver helper/dashboardZoom.js.
    width: rootRect.width || getVisualViewport().width,
    height: rootRect.height || getVisualViewport().height,
  };
}

function closeQuickReact() {
  if (initialShowPicker.value) return;
  if (
    !showQuickReact.value &&
    !quickReactOutsideHandler &&
    !quickReactScrollHandler &&
    !quickReactKeyHandler &&
    !quickReactViewportHandler
  ) {
    return;
  }
  showQuickReact.value = false;
  pillDirection.value = 'up';
  if (quickReactOutsideHandler) {
    document.removeEventListener('pointerdown', quickReactOutsideHandler, true);
    quickReactOutsideHandler = null;
  }
  if (quickReactScrollHandler) {
    document.removeEventListener('scroll', quickReactScrollHandler, true);
    document.removeEventListener('touchmove', quickReactScrollHandler, true);
    quickReactScrollHandler = null;
  }
  if (quickReactKeyHandler) {
    document.removeEventListener('keydown', quickReactKeyHandler);
    quickReactKeyHandler = null;
  }
  if (quickReactViewportHandler) {
    window.removeEventListener('resize', quickReactViewportHandler);
    window.visualViewport?.removeEventListener(
      'resize',
      quickReactViewportHandler
    );
    window.visualViewport?.removeEventListener(
      'scroll',
      quickReactViewportHandler
    );
    quickReactViewportHandler = null;
  }
}

const openTileContextMenu = (event, item) => {
  activeContextItem.value = item;
  tileContextMenuPosition.value = computeMenuAnchor(event, {
    anchor: event.currentTarget,
    orientation: orientation.value,
  });
  showTileContextMenu.value = true;
};

const closeTileContextMenu = () => {
  const wasFromPicker = initialShowPicker.value;
  showTileContextMenu.value = false;
  initialShowPicker.value = false;
  // Aceleres: se o menu nasceu do "+" da pill (ver openFullPickerFromPill),
  // fechar o menu tambem fecha a pill que ficou "logicamente aberta" por
  // baixo dele — mesmo comportamento de closeContextMenu em Message.vue.
  if (wasFromPicker) closeQuickReact();
};

// Aceleres: mesmo cálculo do contextMenuEnabledOptions em Message.vue, mas
// simplificado pro que um sticker individual realmente oferece — sem os
// ramos de texto/edição/nota (copy, edit, cannedResponse, translate,
// addToNote), que já são `false` pra qualquer sticker mesmo na bolha normal.
const tileEnabledOptions = computed(() => {
  const message = activeContextItem.value?.message;
  if (!message) return {};

  const isFailedOrProcessing =
    message.status === MESSAGE_STATUS.FAILED ||
    message.status === MESSAGE_STATUS.PROGRESS;
  const isDeleted = !!message.contentAttributes?.deleted;

  return {
    info: !isFailedOrProcessing && !isDeleted,
    react: !isMessenger.value && !isFailedOrProcessing && !isDeleted,
    download: false,
    attachmentType: 'image',
    copy: false,
    edit: false,
    delete: false,
    cannedResponse: false,
    copyLink: !isFailedOrProcessing,
    translate: false,
    replyTo:
      !message.private &&
      !!inboxSupportsReplyTo.value?.outgoing &&
      !isFailedOrProcessing,
    forward: !isFailedOrProcessing && !isDeleted,
    star: !isFailedOrProcessing && !isDeleted,
    addToNote: false,
    pinned: isMessagePinned(
      conversationGetter.value(message.conversationId),
      message.id
    ),
  };
});

// Aceleres: MessageContextMenu.vue (o componente de menu) espera a mensagem
// no MESMO formato que Message.vue monta em `payloadForContextMenu` — chaves
// de topo em snake_case (content_attributes, conversation_id, message_type…),
// igual o resto do app grava no Vuex. `activeContextItem.message` aqui é
// camelCase (veio de useCamelCase em MessageList.vue), então precisa
// converter — sem `deep`, igual ao payloadForContextMenu: só o nível de
// topo, o que tem dentro de content_attributes/attachments continua como
// está.
//
// Cai pro primeiro sticker do par quando ainda não clicaram em nenhuma
// flechinha (`activeContextItem` null) — de propósito, ver comentário no
// `v-if` do <ContextMenu> no template: ele precisa nascer montado com
// isOpen=false, não no mesmo instante em que vira true.
const contextMenuMessage = computed(() => {
  const message =
    activeContextItem.value?.message ?? albumItems.value[0]?.message;
  return message ? useSnakeCase(message) : null;
});

const handleTileReplyTo = () => {
  const message = activeContextItem.value?.message;
  if (!message) return;

  LocalStorage.updateJsonStore(
    LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
    message.conversationId,
    message.id
  );
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, message);
};

const handleTileReact = emoji => {
  if (isMessenger.value) return;
  const message = activeContextItem.value?.message;
  if (!message) return;

  store.dispatch('reactToMessage', {
    accountId: currentAccountIdGetter.value,
    messageId: message.id,
    emoji,
  });
};

// Aceleres: versao simplificada de positionQuickReactPill (Message.vue) —
// sem o ramo de "isTextOnlyMessage" porque sticker nunca cai nele, entao a
// pill sempre tenta abrir pra cima, clampada ao viewport.
function positionQuickReactPill(smile) {
  const root = quickReactRef.value;
  if (!root || !smile) return;

  const rect = smile.getBoundingClientRect();
  const {
    zoom: pageZoom,
    width: viewportWidth,
    height: viewportHeight,
  } = getQuickReactViewportSize();
  const pillW = (root.offsetWidth || 280) * pageZoom;
  const pillH = (root.offsetHeight || 48) * pageZoom;
  const GAP = 4;
  const PAD = 8;
  const anchorTop = rect.top;
  const anchorCx = rect.left + rect.width / 2;
  const visualTop = Math.min(
    Math.max(anchorTop - pillH - GAP, PAD),
    Math.max(PAD, viewportHeight - pillH - PAD)
  );
  const visualCenterX = Math.min(
    Math.max(anchorCx, PAD + pillW / 2),
    viewportWidth - PAD - pillW / 2
  );

  pillStyle.value = {
    left: fixedPosition(visualCenterX, pageZoom),
    top: fixedPosition(visualTop, pageZoom),
    bottom: '',
    visibility: 'visible',
  };
  pillDirection.value = 'up';
}

function installQuickReactListeners() {
  setTimeout(() => {
    if (!showQuickReact.value) return;
    quickReactOutsideHandler = event => {
      const root = quickReactRef.value;
      if (initialShowPicker.value) return;
      if (root && !root.contains(event.target)) {
        closeQuickReact();
      }
    };
    document.addEventListener('pointerdown', quickReactOutsideHandler, true);
  }, 0);

  quickReactScrollHandler = () => closeQuickReact();
  document.addEventListener('scroll', quickReactScrollHandler, true);
  document.addEventListener('touchmove', quickReactScrollHandler, {
    capture: true,
    passive: true,
  });
  quickReactKeyHandler = event => {
    if (event.key === 'Escape') closeQuickReact();
  };
  document.addEventListener('keydown', quickReactKeyHandler);
  quickReactViewportHandler = () => closeQuickReact();
  window.addEventListener('resize', quickReactViewportHandler, {
    passive: true,
  });
  window.visualViewport?.addEventListener('resize', quickReactViewportHandler, {
    passive: true,
  });
  window.visualViewport?.addEventListener('scroll', quickReactViewportHandler, {
    passive: true,
  });
}

const toggleQuickReact = (e, item) => {
  if (isMessenger.value) return;
  e?.stopPropagation?.();
  if (showQuickReact.value && activeContextItem.value === item) {
    closeQuickReact();
    return;
  }
  activeContextItem.value = item;
  const smile = e?.currentTarget || e?.target;
  pillStyle.value = { ...pillStyle.value, visibility: 'hidden' };
  showQuickReact.value = true;
  nextTick(() => positionQuickReactPill(smile));
  installQuickReactListeners();
};

const openQuickReactFromContextMenu = () => {
  const item = activeContextItem.value;
  const smile = albumGridRef.value
    ?.querySelector(`[data-message-id="${item?.message?.id}"]`)
    ?.querySelector('.aceleres-bubble-smile');
  if (!item || !smile) return;

  closeTileContextMenu();
  nextTick(() => toggleQuickReact({ currentTarget: smile }, item));
};

const handleQuickReact = emoji => {
  handleTileReact(emoji);
  closeQuickReact();
};

// Aceleres: "+" da pill abre o MESMO ContextMenu (menu completo) ja usado
// pela flechinha, direto na aba de emoji — reaproveita computeMenuAnchor
// (mesma funcao que a flechinha usa) ancorada na propria pill, em vez de
// duplicar o calculo de posicao do picker que Message.vue tem pra isso.
const openFullPickerFromPill = () => {
  const anchor = quickReactRef.value;
  if (!anchor) return;
  tileContextMenuPosition.value = computeMenuAnchor(
    { clientX: 0, clientY: 0 },
    { anchor, orientation: orientation.value }
  );
  initialShowPicker.value = true;
  showTileContextMenu.value = true;
  closeQuickReact();
};

const showGallery = ref(false);
const clickedAttachment = ref(null);

const openGalleryAt = item => {
  // Aceleres 2026-08-17: a vetorial deixou de ser recusada aqui. O visor passou
  // a saber desenha-la (GalleryView reconhece o .json e usa o mesmo player da
  // bolha), entao nao ha mais motivo para o par de figurinhas abrir uma e a
  // outra nao.
  clickedAttachment.value = item.attachment;
  showGallery.value = true;
};

const galleryAttachment = computed(() =>
  clickedAttachment.value ? useSnakeCase(clickedAttachment.value) : null
);

// No computador o album abre cada figurinha isolada. No telemovel,
// GalleryView recebe o historico completo por mobileAttachments.
const albumGalleryAttachments = computed(() =>
  galleryAttachment.value ? [galleryAttachment.value] : []
);
</script>

<template>
  <BaseBubble
    class="acl-sticker-album !bg-transparent !p-0 !shadow-none overflow-visible"
    data-bubble-name="sticker-album"
    hide-meta
    hide-sender-name
  >
    <div
      ref="albumGridRef"
      class="acl-sticker-album__grid grid gap-[50px]"
      :style="{
        gridTemplateColumns: `repeat(2, minmax(0, ${STICKER_BOX_PX}px))`,
      }"
    >
      <div
        v-for="item in albumItems"
        :key="item.attachment.id"
        class="acl-sticker-album__tile group relative flex min-w-0 flex-col"
        :data-message-id="item.message.id"
      >
        <!-- Aceleres: três blocos EMPILHADOS, cada um com seu próprio
             fundo/arredondado — nome, imagem e horário NÃO dividem um
             cartão só. Print da conversa real (sticker sozinho) mostrou
             o nome com seu próprio fundinho verde/branco, separado da
             imagem por um vão — igual o horário já tinha ficado
             (commit anterior). -->
        <!-- Aceleres: wrapper SEMPRE presente (não `v-if`) mesmo quando não
             há nome resolvido — é ele que dá o `relative` pra flechinha.
             Se o `v-if` estivesse aqui em vez de no <p>, uma mensagem sem
             sender resolvido perderia a flechinha inteira, sem jeito de
             abrir o menu daquele sticker. -->
        <div class="relative">
          <p
            v-if="senderName"
            class="acl-sender-name m-0 w-full truncate rounded-lg px-[14px] py-[10px] text-[16.2px] font-semibold leading-tight text-n-slate-12 opacity-90 max-md:!overflow-visible max-md:!whitespace-normal max-md:!break-words max-md:!text-clip"
            :class="[tileBackgroundClass, groupSenderColor]"
            :style="{ maxWidth: `${STICKER_BOX_PX}px` }"
          >
            {{ senderNameLabel }}
          </p>
          <!-- Aceleres: flechinha de cada sticker do par — mesmo visual
               (badge circular) e mesmo gatilho de hover que a bolha de
               sticker sozinho já usa (ver `isStickerBubble` em
               Message.vue). Fica em cima do NOME (canto sup. direito do
               tile), não da imagem — pedido explícito, já que agora nome
               e imagem são blocos separados. Aparece só no hover DESTE
               tile (`group`/`group-hover`), não do par inteiro, já que
               cada sticker é sua própria mensagem. Abre o MESMO
               ContextMenu que a bolha normal usa, apontando pra esse
               sticker especificamente. -->
          <button
            type="button"
            aria-label="Abrir menu do sticker"
            class="skip-context-menu absolute right-1.5 top-0 z-20 flex h-[44px] w-[44px] items-center justify-center rounded-full bg-transparent text-n-slate-11 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 max-md:!hidden"
            :class="{
              'acl-received-media-chevron': orientation === ORIENTATION.LEFT,
            }"
            @click.stop="openTileContextMenu($event, item)"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="pointer-events-none block"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
        <!-- Aceleres: SEM tileBackgroundClass aqui de propósito — o cartão
             da imagem fica transparente, deixando o papel de parede do
             chat aparecer através das partes vazadas do sticker (a maioria
             é webp com fundo transparente). O verde/branco fica só nas
             pills de nome e horário, que são texto e precisam de fundo pra
             ler bem. -->
        <div
          class="acl-sticker-album__card relative mt-1 overflow-hidden"
          :class="tileRoundedClass"
        >
          <!-- `relative` para o StickerLoopCanvas se ancorar NESTA caixa (a
               da figurinha) e nao no cartao inteiro, que e maior. -->
          <div
            class="relative flex aspect-square w-full items-center justify-center overflow-hidden"
            :style="{
              maxWidth: `${STICKER_BOX_PX}px`,
              maxHeight: `${STICKER_BOX_PX}px`,
            }"
          >
            <div
              v-if="item.isLottie"
              class="flex size-full cursor-pointer items-center justify-center"
              @click.stop="openGalleryAt(item)"
            >
              <LottieStickerCanvas
                :url="item.attachment.dataUrl"
                class="skip-context-menu h-full w-full"
                replay-on-hover
              />
            </div>
            <!-- Aceleres 2026-08-17: o clique saiu do <img> e passou para
                 esta caixa. Quando a figurinha e um WebP animado, o
                 StickerLoopCanvas assume o desenho e o <img> fica com
                 `visibility: hidden` — e elemento invisivel por `visibility`
                 nao recebe clique, entao o "abrir na galeria" morreria ali.
                 Na caixa ele funciona nos dois estados. -->
            <template v-else>
              <div
                class="flex size-full cursor-pointer items-center justify-center"
                @click.stop="openGalleryAt(item)"
              >
                <img
                  class="block max-h-full max-w-full object-contain skip-context-menu"
                  :class="{
                    invisible: activeStickerCanvas[item.attachment.id],
                  }"
                  :src="item.attachment.dataUrl"
                  loading="lazy"
                />
              </div>
              <StickerLoopCanvas
                :url="item.attachment.dataUrl"
                replay-on-hover
                @active="activeStickerCanvas[item.attachment.id] = true"
                @inactive="activeStickerCanvas[item.attachment.id] = false"
              />
            </template>
          </div>
        </div>
        <!-- Aceleres: pill do horário, separada do cartão (mt-1 de vão),
             encolhida ao próprio conteúdo (self-end) e com fundo/cor
             próprios — igual ao sticker sozinho. -->
        <div
          class="mt-1 flex items-center gap-0.5 self-end rounded-lg px-2 py-0.5 text-[13px] leading-none text-n-slate-11"
          :class="tileBackgroundClass"
        >
          <time>{{ readableTime(item.message) }}</time>
          <MessageStatus
            v-if="statusToShow(item.message)"
            :status="statusToShow(item.message)"
          />
        </div>
        <!-- A pilula de cada sticker acompanha a direção da mensagem: direita
             no enviado e esquerda no recebido, igual às demais bolhas. -->
        <MessageReactions
          v-if="reactionsOf(item.message).length"
          :reactions="reactionsOf(item.message)"
          class="acl-sticker-album__reactions !-mt-[4px] flex"
          :class="orientation === ORIENTATION.RIGHT ? 'self-end' : 'self-start'"
          @open="element => openTileReactions(element, item)"
        />
        <!-- Aceleres 2026-08-11: "carita" de reação rápida — pedido: no
             MESMO lugar que a carita do texto, ao lado do card, centralizada
             na vertical (não mais no canto do nome). Reusa a classe global
             `.aceleres-bubble-smile` (Message.vue) tal e qual, com o mesmo
             `is-right-bubble`/`is-left-bubble` por orientação — sem duplicar
             CSS, só ancorada neste tile (`relative` no `.acl-sticker-album__tile`
             acima) em vez da bolha inteira. -->
        <button
          v-if="!isMessenger && reactionChannelType !== 'Channel::Instagram'"
          type="button"
          aria-label="Reagir ao sticker"
          class="aceleres-bubble-smile skip-context-menu is-without-forward"
          :class="
            orientation === 'right' ? 'is-right-bubble' : 'is-left-bubble'
          "
          @click.stop="toggleQuickReact($event, item)"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="pointer-events-none block"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        </button>
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && galleryAttachment"
    v-model:show="showGallery"
    :attachment="galleryAttachment"
    :all-attachments="albumGalleryAttachments"
    :mobile-attachments="useSnakeCase(currentChatAttachments)"
    is-sticker
    @close="() => (showGallery = false)"
  />
  <!-- Aceleres: MONTA JÁ, mesmo antes de clicar em qualquer flechinha —
       não depende de `showTileContextMenu`. O componente só liga seus
       listeners de auto-fechar (scroll, clique fora, Esc) num `watch`
       de `isOpen` SEM `immediate`, ou seja: só dispara numa TRANSIÇÃO
       false→true depois de montado. Se o componente nascesse junto com
       o primeiro clique (isOpen já true de cara), essa transição nunca
       aconteceria e o menu abriria "surdo" pro scroll na primeira vez —
       era exatamente esse o bug reportado. -->
  <ContextMenu
    v-if="contextMenuMessage"
    :context-menu-position="tileContextMenuPosition"
    :is-open="showTileContextMenu"
    :enabled-options="tileEnabledOptions"
    :message="contextMenuMessage"
    :initial-show-picker="initialShowPicker"
    hide-button
    :current-user-id="currentUserId"
    @close="closeTileContextMenu"
    @reply-to="handleTileReplyTo"
    @react="handleTileReact"
    @open-quick-react="openQuickReactFromContextMenu"
  />
  <!-- Aceleres: mesmo padrão de Message.vue — pill via <Teleport to="body">
       + position:fixed, pra não ser cortada pelo overflow do scroll da
       conversa. `activeContextItem` já guarda qual sticker do par está
       recebendo a reação (setado em `toggleQuickReact`). -->
  <Teleport to="body">
    <MessageReactionsModal
      v-if="showReactionsModal"
      :channel-type="reactionChannelType"
      :reactions="reactionsOf(activeContextItem?.message)"
      :current-user-id="currentUserId"
      :anchor-el="reactionsAnchorEl"
      :orientation="orientation"
      @react="handleTileReact"
      @close="showReactionsModal = false"
    />
  </Teleport>
  <Teleport to="body">
    <Transition name="aceleres-quick-pill">
      <div
        v-if="showQuickReact"
        ref="quickReactRef"
        class="aceleres-quick-react-pill"
        :class="[
          `is-${pillDirection}`,
          { 'is-picker-open': initialShowPicker },
        ]"
        :style="pillStyle"
        @click.stop
      >
        <MessageReactionBar
          :channel-type="reactionChannelType"
          @react="handleQuickReact"
          @more="openFullPickerFromPill"
          @close="closeQuickReact"
        />
      </div>
    </Transition>
  </Teleport>
</template>

<style>
/* Aceleres 2026-08-11: a classe `.aceleres-bubble-smile` e global
   (Message.vue) e por padrao so aparece com `.message-bubble-container:hover`
   — mas aqui o CONTAINER e o par inteiro, entao esse hover revelaria as
   caritas dos DOIS stickers de uma vez. Pedido: so aparecer ao passar por
   cima do STICKER correspondente (a imagem, `.acl-sticker-album__card`),
   nao a linha inteira do tile (nome + imagem + horario) — por isso o
   gatilho e `:hover` no card, nao no tile todo. Como o smile e IRMAO
   posterior do card no DOM (mesmo pai, `.acl-sticker-album__tile`), o
   combinador de irmao geral (`~`) funciona sem precisar de JS.

   O `!important` e necessario, nao so a especificidade dobrada: a regra
   global `.message-bubble-container:hover .aceleres-bubble-smile` tem a
   MESMA especificidade (0,3,0) desta aqui, entao sem `!important` quem
   ganhava era so quem viesse depois na ordem de build do Vite — as vezes
   o CSS global, revelando as DUAS caritas juntas ao passar o mouse em
   QUALQUER lugar do par (container inteiro), nao so no sticker certo. */
.acl-sticker-album__tile.acl-sticker-album__tile .aceleres-bubble-smile {
  opacity: 0 !important;
}
.acl-sticker-album__tile.acl-sticker-album__tile
  .acl-sticker-album__card:hover
  ~ .aceleres-bubble-smile,
.acl-sticker-album__tile.acl-sticker-album__tile .aceleres-bubble-smile:hover,
.acl-sticker-album__tile.acl-sticker-album__tile
  .aceleres-bubble-smile:focus-visible {
  opacity: 1 !important;
}
</style>
